import { Toast as ToastPrimitive } from 'radix-ui'
import type { ComponentPropsWithoutRef, ReactNode } from 'react'
import { useState } from 'react'
import { cn } from '@/shared/lib/cn'
import { copyToClipboard } from '@/shared/lib/clipboard'
import { Icon } from '@/shared/ui/icon'
import s from './Toast.module.css'

type ToastTone = 'error' | 'success'

type ToastProps = Omit<ComponentPropsWithoutRef<typeof ToastPrimitive.Root>, 'type' | 'children'> & {
  tone: ToastTone
  title: ReactNode
  description?: ReactNode
  requestId?: string | null
  report?: string
}

/**
 * Вид — Step 4 брифа буквально (`--panel`/`--line`/radius 12/`--shadow-pop`/полоса 3px по типу/
 * `pop-in`); в макете тоста нет ни одного (см. `ToastProvider.tsx`).
 *
 * Пропсы `tone`/`requestId`/`report` — не придуманы заново, а взяты из формы `Toast`, которую
 * план фазы 1 уже фиксирует для будущего стора Task 18 (docs/superpowers/plans/
 * 2026-07-11-phase-1-foundation-and-shell.md:4530-4537: `{ id, tone, title, description,
 * requestId, report }`), чтобы не переименовывать их, когда стор подключит эти компоненты.
 *
 * Кнопка «Скопировать» копирует `report`, а не `requestId`: по дизайн-спеке (docs/superpowers/
 * specs/2026-07-11-prima-bookings-frontend-design.md:523) тост ошибки копирует «тот же отчёт»,
 * что и экран смерти, — `X-Request-Id` в строке ниже нужен для взгляда, не как то, что летит в
 * буфер обмена. Если `report` ещё нет, а `requestId` есть — копирует его как более скромный
 * фолбэк. Кнопка/строка рендерятся только при наличии хотя бы одного из двух.
 */
export function Toast({
  tone,
  title,
  description,
  requestId,
  report,
  className,
  ...rest
}: ToastProps) {
  const [copied, setCopied] = useState(false)
  const copyText = report ?? requestId ?? undefined

  /**
   * Копируем через `copyToClipboard`, а не напрямую через `navigator.clipboard.writeText`.
   *
   * Прямой вызов был без `.catch()`, а он ОТКАЗЫВАЕТ: вне secure origin, при
   * `NotAllowedError: Document is not focused` (частый случай — человек кликнул в другое окно),
   * при запрете политикой. Отказ давал unhandled rejection → `RootErrorBoundary` → **экран
   * смерти на всё приложение, без обратной дороги**. То есть кнопка, придуманная для отчёта об
   * ошибке, производила ошибку хуже той, о которой отчитывалась.
   *
   * `copyToClipboard` (`shared/lib/clipboard`) не бросает, а возвращает `false`, и у него есть
   * фолбэк на `execCommand`. `DeathScreen` пользуется им с самого начала — тост просто не
   * подключили.
   */
  const handleCopy = async () => {
    if (!copyText) return
    if (!(await copyToClipboard(copyText))) return

    setCopied(true)
    window.setTimeout(() => setCopied(false), 1500)
  }

  return (
    <ToastPrimitive.Root
      type={tone === 'error' ? 'foreground' : 'background'}
      className={cn(s.root, className)}
      data-tone={tone}
      {...rest}
    >
      <div className={s.row}>
        <div className={s.body}>
          <ToastPrimitive.Title className={s.title}>{title}</ToastPrimitive.Title>
          {description && (
            <ToastPrimitive.Description className={s.description}>
              {description}
            </ToastPrimitive.Description>
          )}
          {(requestId || copyText) && (
            <div className={s.meta}>
              {requestId && <span className={s.requestId}>X-Request-Id: {requestId}</span>}
              {copyText && (
                <button type="button" className={s.copy} onClick={() => void handleCopy()}>
                  <Icon name={copied ? 'check' : 'copy'} size={11} />
                  Скопировать
                </button>
              )}
            </div>
          )}
        </div>
        <ToastPrimitive.Close className={s.close} aria-label="Закрыть">
          <Icon name="close" size={12} />
        </ToastPrimitive.Close>
      </div>
    </ToastPrimitive.Root>
  )
}
